import isEqual from 'lodash.isequal';
import moment from 'moment';
import { DepositType, InvoiceStatus, Placement, } from '@wix/ambassador-wix-quotes-web/types';
import { addressFormatter } from '@wix/crm-financial-formatters';
import { getCurrencyByKey } from '@wix/locale-dataset-javascript';
import { storeProductVariantAppendix } from '../../utils/regex';
import { parseFromHtmlString, parseJSON, parseSerializedValue, } from '../../utils/parsing';
export const getFormattedIssueDate = ({ isRecurring, issueDate, status, }) => {
    const shouldModifyDatesForRecurringSpecialCase = isRecurring && status === InvoiceStatus.Draft;
    return shouldModifyDatesForRecurringSpecialCase
        ? moment().startOf('day').utc().toDate()
        : new Date(issueDate);
};
const diffInDaysFloored = ({ dateA, dateB }) => Math.floor(moment.duration(moment(dateA).diff(dateB)).as('days'));
export const getFormattedDueDate = ({ dueDate, isRecurring, issueDate, status, }) => {
    const shouldModifyDatesForRecurringSpecialCase = isRecurring && status === InvoiceStatus.Draft;
    const dueDateOffset = shouldModifyDatesForRecurringSpecialCase
        ? diffInDaysFloored({
            dateA: dueDate,
            dateB: issueDate,
        })
        : null;
    return shouldModifyDatesForRecurringSpecialCase
        ? moment().add(dueDateOffset, 'days').startOf('day').utc().toDate()
        : new Date(dueDate);
};
const mapLineItemMetadata = ({ metadata }) => {
    const metadataJsonMap = metadata?.metadata ?? {};
    const selectedOptions = parseJSON(metadataJsonMap.selectedOptions)?.reduce((accObj, { option, selection }) => {
        if (option && selection) {
            accObj[option] = selection;
        }
        return accObj;
    }, {});
    try {
        return {
            ...metadataJsonMap,
            ...(selectedOptions ? { selectedOptions } : {}),
        };
    }
    catch {
        return metadataJsonMap;
    }
};
export const mapLineItemDtoToViewModelMapper = (lineItems) => lineItems.length
    ? lineItems.map((lineItem, i) => {
        const metadata = mapLineItemMetadata(lineItem);
        return {
            id: lineItem.id ?? i.toString(),
            name: lineItem.name ?? '',
            description: parseFromHtmlString(lineItem.description?.replace(storeProductVariantAppendix(Object.keys(metadata.selectedOptions ?? {})), ''), undefined),
            metadata,
            price: parseSerializedValue(lineItem.price) || 0,
            quantity: parseSerializedValue(lineItem.quantity) || 0,
            taxedTotal: parseSerializedValue(lineItem.taxedTotal) || 0,
            taxes: lineItem.taxes?.length
                ? lineItem.taxes.map((tax) => ({
                    name: tax.name ?? '',
                    rate: parseSerializedValue(tax.rate) || 0,
                    taxId: tax.code ?? '',
                }))
                : [],
            source: lineItem.source ?? {},
        };
    })
    : [];
export const mapInvoiceDetailsFromDTO = ({ invoice, recurringInvoiceViewEnabled, status = InvoiceStatus.Draft, }) => {
    const { currencyCode = 'USD', customer = {}, dates = {}, deposit = {}, discount = {}, dynamicTotals = {}, lineItems = {}, locale = {}, metaData = {}, number: invoiceNumber = '', taxes: calculatedTaxes = {}, title = '', totals = {}, payments = {}, } = invoice ?? {};
    const depositRate = Number(deposit.percentage) || 0;
    return {
        balance: parseSerializedValue(dynamicTotals.balance) || 0,
        currency: currencyCode,
        customer,
        deposit: {
            amount: Number(deposit.flatAmount) || 0,
            rate: (depositRate * 100).toFixed(getCurrencyByKey(currencyCode).fraction),
            type: deposit.type ?? DepositType.UNKNOWN,
        },
        discount: {
            amount: parseSerializedValue(totals.discountAmount) || 0,
            rate: parseSerializedValue(discount.percentage) || 0,
        },
        dueDate: dates.dueDate && dates.issueDate
            ? getFormattedDueDate({
                dueDate: dates.dueDate,
                isRecurring: !!recurringInvoiceViewEnabled,
                issueDate: dates.issueDate,
                status,
            })
            : undefined,
        fees: totals.fees?.length
            ? totals.fees.map((fee) => ({
                name: fee.name ?? '',
                price: parseSerializedValue(fee.price) || 0,
            }))
            : [],
        invoiceNumber,
        issueDate: dates.issueDate
            ? getFormattedIssueDate({
                isRecurring: !!recurringInvoiceViewEnabled,
                issueDate: dates.issueDate,
                status,
            })
            : undefined,
        lineItems: lineItems.lineItems?.length
            ? mapLineItemDtoToViewModelMapper(lineItems.lineItems)
            : [],
        locale: locale?.language ?? 'en',
        metaData,
        paid: parseSerializedValue(dynamicTotals.paidAmount) || 0,
        status,
        subtotal: parseSerializedValue(totals.subtotal) || 0,
        taxes: calculatedTaxes.taxes?.length
            ? calculatedTaxes.taxes.map((tax) => ({
                amount: parseSerializedValue(tax.taxed) || 0,
                name: tax.name ?? '',
                rate: parseSerializedValue(tax.rate) || 0,
                taxable: parseSerializedValue(tax.taxable) || 0,
            }))
            : [],
        title,
        total: parseSerializedValue(totals.total) || 0,
        taxedTotal: parseSerializedValue(totals.taxedAmount) || 0,
        payments: payments.payments || [],
    };
};
export const formatBusinessAddress = ({ address, description } = {}, t) => {
    if (!address) {
        return [];
    }
    const localizedLocation = addressFormatter({
        city: address.city ?? '',
        country: address.country ?? '',
        state: address.subdivision ?? '',
        street: address.addressLine,
        streetName: address.streetAddress?.name,
        streetNumber: address.streetAddress?.number,
        apt: address.streetAddress?.apt,
        zip: address.postalCode ?? '',
        t,
    });
    switch (description?.placement) {
        case Placement.Replace:
            return [description.content ? description.content : localizedLocation];
        case Placement.Before:
            return [description.content || '', localizedLocation];
        case Placement.After:
            return [localizedLocation, description.content || ''];
        default:
            return [localizedLocation];
    }
};
// TODO: review if it is possible to simplify
export const formatCustomerAddresses = ({ address, billingAddress, company, email, name, phone, shippingAddress, }, t) => {
    const formatAddress = (args) => addressFormatter({
        t,
        ...args,
    });
    const addressLocalizedLocation = address &&
        formatAddress({
            city: address.city ?? '',
            country: address.country ?? '',
            state: address.state ?? '',
            street: address.street,
            zip: address.zip ?? '',
        });
    const billingAddressLocalizedLocation = billingAddress &&
        formatAddress({
            city: billingAddress.city ?? '',
            country: billingAddress.country ?? '',
            state: billingAddress.subdivision ?? '',
            street: billingAddress.addressLine,
            zip: billingAddress.postalCode ?? '',
        });
    const shippingLocalizedAddress = shippingAddress &&
        formatAddress({
            city: shippingAddress.city ?? '',
            country: shippingAddress.country ?? '',
            state: shippingAddress.subdivision ?? '',
            street: shippingAddress.addressLine,
            zip: shippingAddress.postalCode ?? '',
        });
    const shouldDisplayShippingAddress = !!(shippingAddress &&
        shippingLocalizedAddress &&
        !isEqual(shippingAddress, billingAddress) &&
        !isEqual(shippingLocalizedAddress, addressLocalizedLocation));
    const shouldDisplayCustomerAddress = (billingAddressLocalizedLocation || addressLocalizedLocation) !== '';
    const hasAddress = !!(shouldDisplayCustomerAddress || shouldDisplayShippingAddress);
    const infoSections = [];
    const recipient = [name, company?.name].filter(Boolean).join('\n');
    if (shouldDisplayCustomerAddress) {
        infoSections.push({
            title: t('price-quote.view.customer.billing-address-title'),
            subtitle: recipient,
            content: [billingAddressLocalizedLocation || addressLocalizedLocation],
        });
    }
    if (shouldDisplayShippingAddress) {
        infoSections.push({
            title: !shouldDisplayCustomerAddress
                ? t('price-quote.view.customer.billing-address-title')
                : t('price-quote.view.customer.shipping-address-title'),
            subtitle: shouldDisplayCustomerAddress ? '' : recipient,
            content: [shippingLocalizedAddress],
        });
    }
    infoSections.push({
        title: hasAddress
            ? t('price-quote.view.customer.additional-info-title')
            : t('price-quote.view.customer.customer-info-title'),
        subtitle: hasAddress ? '' : recipient,
        content: [
            email?.address,
            phone?.number &&
                t('price-quote.view.customer.phone-title', {
                    phoneNumber: phone.number,
                }),
            company?.id && `${t('quote.form.vat_id')}: ${company.id}`,
        ],
    });
    return infoSections;
};
//# sourceMappingURL=utils.js.map